import { useState, useEffect } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Megaphone,
  ArrowLeft,
  Loader2,
  AlertCircle,
  Save
} from 'lucide-react'
import { announcementApi, type AnnouncementRequest, type AnnouncementStatus, type AppType } from '@/api/announcements'
import { adminApi } from '@/api/admin'
import { TiptapEditor } from '@/components/editor/TiptapEditor'

/** UTC ISO 문자열을 로컬 datetime-local 입력 포맷(YYYY-MM-DDTHH:mm)으로 변환 */
function toLocalDatetime(isoString: string | null | undefined): string {
  if (!isoString) return ''
  const d = new Date(isoString)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

const STATUS_OPTIONS: { value: AnnouncementStatus; label: string }[] = [
  { value: 'draft', label: '초안' },
  { value: 'published', label: '게시' },
  { value: 'scheduled', label: '예약' },
]

export function AnnouncementEditPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const appTypeFromUrl = (searchParams.get('appType') || 'company') as AppType
  const isEditMode = id !== undefined && id !== 'new'

  const [formData, setFormData] = useState<AnnouncementRequest>({
    title: '',
    content: '',
    status: 'draft',
    isPinned: false,
    isGlobal: true,
    targetAppTypes: [appTypeFromUrl],
    targetOrganizationIds: [],
    targetAudience: null,
    publishStartDate: null,
    publishEndDate: null,
  })

  // Fetch announcement if editing
  const { data: announcement, isLoading: loadingAnnouncement } = useQuery({
    queryKey: ['admin', 'announcement', id],
    queryFn: () => announcementApi.getById(Number(id)),
    enabled: isEditMode,
  })

  // Fetch organizations for targeting
  const { data: organizations } = useQuery({
    queryKey: ['admin', 'organizations'],
    queryFn: () => adminApi.getOrganizations(),
    staleTime: 5 * 60 * 1000,
  })

  // Populate form when editing
  useEffect(() => {
    if (announcement) {
      setFormData({
        title: announcement.title,
        content: announcement.content,
        status: announcement.status,
        isPinned: announcement.isPinned,
        isGlobal: announcement.isGlobal,
        targetAppTypes: announcement.targetAppTypes || [appTypeFromUrl],
        targetOrganizationIds: announcement.targetOrganizationIds || [],
        targetAudience: announcement.targetAudience || null,
        publishStartDate: announcement.publishStartAt,
        publishEndDate: announcement.publishEndAt,
      })
    }
  }, [announcement, appTypeFromUrl])

  const createMutation = useMutation({
    mutationFn: announcementApi.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] })
      navigate('/announcements')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AnnouncementRequest }) =>
      announcementApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'announcement', id] })
      navigate(`/announcements/${id}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (isEditMode) {
      updateMutation.mutate({ id: Number(id), data: formData })
    } else {
      createMutation.mutate(formData)
    }
  }

  const handleOrganizationChange = (orgId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      targetOrganizationIds: checked
        ? [...(prev.targetOrganizationIds || []), orgId]
        : (prev.targetOrganizationIds || []).filter(id => id !== orgId),
    }))
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending

  if (isEditMode && loadingAnnouncement) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-slate-600 animate-spin mb-4" />
        <p className="text-slate-600">공지사항을 불러오는 중...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-slate-100 rounded-lg"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="w-7 h-7 text-blue-600" />
            {isEditMode ? '공지사항 수정' : '새 공지사항 작성'}
          </h1>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="label">제목 *</label>
            <input
              type="text"
              className="input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="공지사항 제목을 입력하세요"
              required
            />
          </div>

          {/* App Type Indicator */}
          <div>
            <label className="label">대상 앱</label>
            <div className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 w-fit">
              {appTypeFromUrl === 'company' ? '기업 B2B' : appTypeFromUrl === 'lc' ? '러닝센터 B2B' : 'B2C'}
            </div>
          </div>

          {/* Content */}
          <div>
            <label className="label">내용 *</label>
            <TiptapEditor
              content={formData.content}
              onChange={(html) => setFormData({ ...formData, content: html })}
              placeholder="공지사항 내용을 입력하세요..."
            />
          </div>

          {/* Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">상태 *</label>
              <select
                className="input"
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value as AnnouncementStatus })}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Publish Start Date (for scheduled) */}
            {formData.status === 'scheduled' && (
              <div>
                <label className="label">게시 시작일 *</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={toLocalDatetime(formData.publishStartDate)}
                  onChange={(e) => setFormData({ ...formData, publishStartDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  required={formData.status === 'scheduled'}
                />
              </div>
            )}
          </div>

          {/* Publish End Date */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="label">게시 종료일 (선택)</label>
              <input
                type="datetime-local"
                className="input"
                value={toLocalDatetime(formData.publishEndDate)}
                onChange={(e) => setFormData({ ...formData, publishEndDate: e.target.value ? new Date(e.target.value).toISOString() : null })}
              />
            </div>
          </div>

          {/* Checkboxes */}
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isPinned}
                onChange={(e) => setFormData({ ...formData, isPinned: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">상단 고정</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.isGlobal}
                onChange={(e) => setFormData({ ...formData, isGlobal: e.target.checked })}
                className="w-4 h-4 rounded border-slate-300"
              />
              <span className="text-sm text-slate-700">전체 클라이언트 공지 (모든 조직)</span>
            </label>
          </div>

          {/* Target Audience (Company / LC B2B) */}
          {(formData.targetAppTypes?.includes('company') || formData.targetAppTypes?.includes('lc')) && (
            <div>
              <label className="label">대상 역할</label>
              <div className="flex flex-wrap gap-4">
                {[
                  { value: '', label: '전체 (관리자 + 학습자)' },
                  { value: 'admin', label: '관리자만' },
                  { value: 'learner', label: '학습자만' },
                ].map((option) => (
                  <label key={option.value} className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="targetAudience"
                      checked={(formData.targetAudience || '') === option.value}
                      onChange={() => setFormData({ ...formData, targetAudience: option.value || null })}
                      className="w-4 h-4 border-slate-300"
                    />
                    <span className="text-sm text-slate-700">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Target Organizations (if not global, filtered by app type) */}
          {!formData.isGlobal && organizations && organizations.length > 0 && (
            <div>
              <label className="label">대상 조직</label>
              <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-3 space-y-2">
                {organizations.filter((org) => {
                  const appTypes = formData.targetAppTypes || []
                  if (appTypes.length === 0) return true
                  if (appTypes.includes('company') && org.organizationType === 'company') return true
                  if (appTypes.includes('lc') && org.organizationType === 'learning_center') return true
                  return false
                }).map((org) => (
                  <label key={org.id} className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.targetOrganizationIds?.includes(org.id) ?? false}
                      onChange={(e) => handleOrganizationChange(org.id, e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300"
                    />
                    <span className="text-sm text-slate-700">
                      {org.displayName || org.name}
                      <span className="text-slate-400 ml-1">({org.organizationType})</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Error Display */}
        {(createMutation.error || updateMutation.error) && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">
              {(createMutation.error || updateMutation.error)?.message || '오류가 발생했습니다'}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="btn-secondary btn-md"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-primary btn-md inline-flex items-center gap-2"
          >
            {isSubmitting ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            {isEditMode ? '수정' : '저장'}
          </button>
        </div>
      </form>
    </div>
  )
}
